import React, { FC, ReactNode, useState } from 'react';
import BackeeMode from '@backee/backeeMode';
import { observer } from 'mobx-react';
import CheckboxCardsList from '@app/components/CheckboxCardsList';
import PermissionListItem from '@app/components/PermissionListItem';
import useInfraStores from '@app/hooks/useInfraStores';
import InputBox from '@app/components/inputs/inputBox/InputBox';
import NakedFormInput from '@app/components/inputs/NakedFormInput';
import { useLoadableSearchFilter } from '@app/hooks/useSearchFilter';
import { StagedIds } from '@app/hooks/useStagedIds';
import SaveCancelActions from '@app/components/SaveCancelActions';
import { IdentityProvider, UpdateOrganizationIdentityProvidersRequest } from '@app/domain/userManagement/identityProviders';
import Loadable from '@app/utils/Loadable';

interface Props {
  id: string;
  dataTestId: string;
  identityProviders: Loadable<IdentityProvider[]>;
  stagedIds: StagedIds;
  className?: string;
  onSave(change: UpdateOrganizationIdentityProvidersRequest): Promise<void>;
}

const ManageOrganizationIdentityProviders: FC<Props> = observer(
  ({ id, dataTestId, identityProviders, stagedIds, onSave, className }) => {
    const { permissionsStore } = useInfraStores<BackeeMode>();

    const [filteredData, searchTerm, setSearchTerm] = useLoadableSearchFilter(identityProviders, {
      partialMatches(identityProvider) {
        return [identityProvider.name];
      },
    });

    const {
      activeIds,
      addedIds,
      removedIds,
      changedIds,
      hasChanges,
      maxChanges,
      exceedsMaxChanges,
      stageAdd,
      stageRemove,
      discard,
    } = stagedIds;
    const [isSaving, setIsSaving] = useState<boolean>(false);

    async function onSaveRequest(): Promise<void> {
      setIsSaving(true);

      try {
        await onSave({ addedIdentityProviderNames: addedIds, removedIdentityProviderNames: removedIds });
      } finally {
        setIsSaving(false);
      }
    }

    return (
      <div>
        <InputBox appearance='line' colorScheme='secondary'>
          <NakedFormInput
            name={`${id}-search`}
            dataTestId={`${dataTestId}-search`}
            type='text'
            colorScheme='primary'
            placeholderStyle='onlyWhenEmpty'
            heightType='thick'
            clearable
            placeholder='🔍 Search for Identity Provider Name'
            value={searchTerm}
            onChange={(newValue): void => setSearchTerm(newValue ?? '')}
            autoFocus
          />
        </InputBox>
        <CheckboxCardsList
          id={id}
          dataTestId={dataTestId}
          contentColumnTitle='IdentityProviders'
          readonly={!permissionsStore.isEditAllowed || isSaving}
          activeIds={activeIds}
          changedIds={changedIds}
          changingIds={[]}
          allItems={filteredData}
          itemRenderer={(identityProvider: IdentityProvider): ReactNode => {
            return <PermissionListItem permissionsId={identityProvider.name} displayName={identityProvider.type} />;
          }}
          itemKeywordsGetter={(identityProvider: IdentityProvider): string[] => {
            return [identityProvider.id, identityProvider.name];
          }}
          onAdd={async (identityProviderName): Promise<void> => stageAdd(identityProviderName)}
          onRemove={async (identityProviderName): Promise<void> => stageRemove(identityProviderName)}
          className={className}
        />
        {permissionsStore.isEditAllowed && (
          <SaveCancelActions
            cancelId={`btn-${id}-cancel`}
            saveId={`btn-${id}-save`}
            hasChanges={hasChanges}
            maxChanges={maxChanges}
            exceedsMaxChanges={exceedsMaxChanges}
            isSaving={isSaving}
            isCancelDisabled={!hasChanges}
            onCancel={discard}
            onSave={onSaveRequest}
          />
        )}
      </div>
    );
  },
);

export default ManageOrganizationIdentityProviders;
